import { useLazyQuery } from "@apollo/client";
import { IonBadge, IonItem, IonItemDivider, IonItemGroup, IonLabel, IonList } from "@ionic/react";
import debounce from "debounce-promise";
import equal from "deep-equal";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SingleValue } from "react-select";

import { RichTextareaContext } from "@components/fields/RichTextareaProvider";
import searchStyles from "@components/fieldsNative/SearchInput.module.css";
import SearchInputWrapper from "@components/fieldsNative/SearchInputWrapper";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMyAdmin from "@hooks/useMyAdmin";
import useMyExpert from "@hooks/useMyExpert";
import {
  ArticleContentTypeEnum,
  GetArticleBasicsDocument,
  GetArticleBasicsQuery,
  GetArticleBasicsQueryVariables,
  GetEmployerBenefitsDocument,
  GetEmployerBenefitsQuery,
  GetEmployerBenefitsQueryVariables,
  GetJourneyWithClientVisibleTasksBasicDocument,
  GetJourneyWithClientVisibleTasksBasicQuery,
  GetJourneyWithClientVisibleTasksBasicQueryVariables,
  GoalStatusEnum
} from "@typing/Generated";
import { sortByLabel } from "@utils/modelUtils";

enum LinkTypes {
  ARTICLES = "articles",
  EMPLOYER_BENEFITS = "employer_benefits",
  GOALS = "goals",
  MY_STORIES = "my_stories",
  PAGES = "pages",
  QUIZZES = "quizzes",
  RESOURCES = "resources",
  TASKS = "tasks",
  WEBINARS = "webinars"
}

type Option = {
  groupLabel: string;
  label: string;
  value: string;
};

type OptionGroup = {
  label: string;
  options: Option[];
};

type Props = {
  onSelect: () => void;
  showArticleLinksEvenWithoutJourneyId?: boolean;
};

type Task = GetJourneyWithClientVisibleTasksBasicQuery["journey"]["goals"][number]["tasks"][number];
type Resource =
  GetJourneyWithClientVisibleTasksBasicQuery["journey"]["goals"][number]["tasks"][number]["resources"][number];

const RichTextareaInAppLinksSelect = ({ onSelect, showArticleLinksEvenWithoutJourneyId }: Props) => {
  const intl = useIntl();
  const myAdmin = useMyAdmin();
  const myExpert = useMyExpert();
  const [defaultOptions, setDefaultOptions] = useState<OptionGroup[]>([]);
  const [options, setOptions] = useState<OptionGroup[]>([]);
  const [query, setQuery] = useState<string>("");
  const setQueryDebounced = debounce(setQuery, 350);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  const { editor, journeyId, setInsertContent } = useContext(RichTextareaContext);

  const [getJourney, getJourneyQuery] = useLazyQuery<
    GetJourneyWithClientVisibleTasksBasicQuery,
    GetJourneyWithClientVisibleTasksBasicQueryVariables
  >(GetJourneyWithClientVisibleTasksBasicDocument);

  const [getEmployerBenefits, employerBenefitsQuery] = useLazyQuery<
    GetEmployerBenefitsQuery,
    GetEmployerBenefitsQueryVariables
  >(GetEmployerBenefitsDocument);

  const getArticlesQuery = useLazyQueryWithPromise<GetArticleBasicsQuery, GetArticleBasicsQueryVariables>(
    GetArticleBasicsDocument
  );

  const goals = getJourneyQuery.data?.journey.goals.filter(goal => goal.status === GoalStatusEnum.ACTIVE);

  const tasksAndResources = useMemo(() => {
    const tasks: Task[] = [];
    const resources: Resource[] = [];
    goals?.forEach(goal => {
      goal.tasks.forEach(task => {
        if (task.clientVisible) {
          tasks.push(task);
          task.resources.forEach(resource => {
            resources.push(resource);
          });
        }
      });
    });
    return {
      resources,
      tasks
    };
  }, [goals]);

  const generateUrl = useCallback(
    (path: string) => {
      if (path.startsWith("http") || path.startsWith("mailto")) {
        return path;
      }
      return `${import.meta.env.VITE_APP_HOST}/client/journeys/${journeyId ? journeyId : "JOURNEY_ID"}${path}`;
    },
    [journeyId]
  );

  const pageOptions = useMemo<Option[]>(() => {
    const pages = [
      { label: "carePlan", value: "/care-plan" },
      { label: "scheduleConsult", value: "/schedule-consult" },
      { label: "message", value: "/messages" },
      { label: "library", value: "/explore" },
      { label: "marketplace", value: "/explore/marketplace" },
      { label: "community", value: "https://gather.withgrayce.com" },
      { label: "events", value: "/explore/events" },
      { label: "drive", value: "/drive" },
      { label: "consentForm", value: "/drive#consent-form" },
      { label: "profile", value: "/care-profile" },
      { label: "account", value: "/account" },
      { label: "termsOfUse", value: "https://www.withgrayce.com/terms" },
      { label: "privacyPolicy", value: "https://www.withgrayce.com/privacy" },
      { label: "faq", value: "https://www.withgrayce.com/faq" },
      { label: "help", value: "mailto:support@withgrayce.com" }
    ];

    return pages.map(option => ({
      groupLabel: LinkTypes.PAGES,
      label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${option.label}` }),
      value: generateUrl(option.value)
    }));
  }, [intl, generateUrl]);

  const pageMatches = useCallback(
    (searchTerm: string): Option[] =>
      pageOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [pageOptions]
  );

  const quizOptions = [
    { label: "medicationTracker", value: "/medication" },
    { label: "legalDocuments", value: "/legal" },
    { label: "providerTracker", value: "/provider" }
  ].map(option => ({
    groupLabel: LinkTypes.QUIZZES,
    label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${option.label}` }),
    value: generateUrl(option.value)
  }));

  const marketplaceOptions = useMemo<Option[]>(() => {
    if (
      !getJourneyQuery.data?.journey.owner.employer?.hideBenefits &&
      employerBenefitsQuery.data?.employerBenefits.edges
    ) {
      return employerBenefitsQuery.data.employerBenefits?.edges.map(benefitNode => ({
        groupLabel: LinkTypes.EMPLOYER_BENEFITS,
        imageName: "marketplace",
        label: benefitNode?.node?.vendorName as string,
        value: generateUrl(`/explore/marketplace?benefitId=${benefitNode?.node?.id}`)
      }));
    }
    return [];
  }, [
    employerBenefitsQuery.data?.employerBenefits,
    getJourneyQuery.data?.journey.owner.employer?.hideBenefits,
    generateUrl
  ]);

  const quizMatches = useCallback(
    (searchTerm: string): Option[] =>
      quizOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [quizOptions]
  );

  const marketplaceMatches = useCallback(
    (searchTerm: string): Option[] =>
      marketplaceOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [marketplaceOptions]
  );

  const loadArticleTypeOptions = useCallback(
    (searchTerm: string) =>
      new Promise<OptionGroup[] | null>(resolve => {
        if (myExpert || (myAdmin && showArticleLinksEvenWithoutJourneyId)) {
          Promise.all([
            getArticlesQuery({
              variables: {
                contentTypes: [
                  ArticleContentTypeEnum.MY_STORY,
                  ArticleContentTypeEnum.TEXT,
                  ArticleContentTypeEnum.VIDEO,
                  ArticleContentTypeEnum.WEBINAR
                ],
                search: searchTerm
              }
            })
          ]).then(data => {
            const modelsByContentType: { [key: string]: { label: string; value: string }[] } = {};

            if (data[0]?.data?.articles?.edges) {
              Object.values(ArticleContentTypeEnum).forEach(contentType => {
                if (data[0].data.articles.edges) {
                  modelsByContentType[contentType.toString()] = data[0].data.articles.edges
                    ?.filter(articleNode => articleNode?.node?.contentType === contentType)
                    .map(articleNode => ({
                      label: articleNode?.node?.title as string,
                      value: generateUrl(`/articles/${articleNode?.node?.id}`)
                    }));
                }
              });
            }

            resolve([
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.ARTICLES}` }),
                options: sortByLabel(
                  modelsByContentType[ArticleContentTypeEnum.TEXT].concat(
                    modelsByContentType[ArticleContentTypeEnum.VIDEO]
                  )
                ).map(option => ({ ...option, groupLabel: LinkTypes.ARTICLES }))
              },
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.MY_STORIES}` }),
                options: sortByLabel(modelsByContentType[ArticleContentTypeEnum.MY_STORY]).map(option => ({
                  ...option,
                  groupLabel: LinkTypes.MY_STORIES
                }))
              },
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.WEBINARS}` }),
                options: sortByLabel(modelsByContentType[ArticleContentTypeEnum.WEBINAR]).map(option => ({
                  ...option,
                  groupLabel: LinkTypes.WEBINARS
                }))
              }
            ]);
          });
        } else {
          resolve(null);
        }
      }),
    [getArticlesQuery, intl, generateUrl, myAdmin, myExpert, showArticleLinksEvenWithoutJourneyId]
  );

  type ThingWithTitleAndId = {
    id: string;
    title: string | null;
  };

  type GenerateItemsProps<D extends ThingWithTitleAndId> = {
    linkType: LinkTypes;
    makeUrl: (thing: D) => string;
    searchTerm: string;
    things: D[];
  };

  const generateItems = useCallback(
    <D extends ThingWithTitleAndId>({ linkType, makeUrl, searchTerm, things }: GenerateItemsProps<D>) => {
      const items = things
        .map(thing => ({
          key: thing.id,
          label: thing.title ?? "",
          value: makeUrl(thing)
        }))
        .filter(thing => thing.label.toLowerCase().includes(searchTerm.toLowerCase()));

      if (items) {
        return {
          label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${linkType}` }),
          options: sortByLabel(items).map(option => ({ ...option, groupLabel: linkType }))
        };
      }
    },
    [intl]
  );

  const optionsForSearch = useCallback(
    (searchTerm: string) =>
      new Promise<OptionGroup[] | null>(resolve => {
        loadArticleTypeOptions(searchTerm).then(articleOptions => {
          const result: OptionGroup[] = [
            {
              label: intl.formatMessage({
                id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.EMPLOYER_BENEFITS}`
              }),
              options: marketplaceMatches(searchTerm).map(option => ({ ...option }))
            },
            {
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.PAGES}` }),
              options: pageMatches(searchTerm).map(option => ({ ...option }))
            },
            {
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.QUIZZES}` }),
              options: quizMatches(searchTerm).map(option => ({ ...option }))
            }
          ];

          if (articleOptions) {
            articleOptions.forEach(option => result.push(option));
          }

          if (goals) {
            const items = generateItems({
              linkType: LinkTypes.GOALS,
              makeUrl: goal => generateUrl(`/care-plan/goals/${goal.id}`),
              searchTerm,
              things: goals
            });
            if (items) {
              result.push(items);
            }
          }

          if (tasksAndResources.tasks) {
            const items = generateItems({
              linkType: LinkTypes.TASKS,
              makeUrl: task => generateUrl(`/client-visible-tasks/${task.id}`),
              searchTerm,
              things: tasksAndResources.tasks
            });
            if (items) {
              result.push(items);
            }
          }

          if (tasksAndResources.resources) {
            const items = generateItems({
              linkType: LinkTypes.RESOURCES,
              makeUrl: resource => generateUrl(`/client-visible-tasks/${resource.task.id}?resource-id=${resource.id}`),
              searchTerm,
              things: tasksAndResources.resources
            });
            if (items) {
              result.push(items);
            }
          }

          resolve(result);
        });
      }),
    [
      loadArticleTypeOptions,
      intl,
      marketplaceMatches,
      pageMatches,
      quizMatches,
      goals,
      tasksAndResources,
      generateItems,
      generateUrl
    ]
  );

  const handleSelect = useCallback(
    (option: SingleValue<Option>) => {
      if (editor && option) {
        const link = [`<a href="${option.value}"`];
        if (option.value.startsWith("https://www.withgrayce.com") || option.value.startsWith("mailto")) {
          link.push(` rel="noopener noreferrer" target="_blank"`);
        }
        link.push(`>${option.label}</a>`);

        setInsertContent(link.join(""));
      }
      onSelect();
    },
    [editor, onSelect, setInsertContent]
  );

  // load all the stuff
  useEffect(() => {
    if (journeyId && myExpert?.id) {
      getJourney({
        variables: {
          id: journeyId
        }
      });
    }
  }, [getJourney, journeyId, myExpert?.id]);

  useEffect(() => {
    if (journeyId && myExpert?.id) {
      getEmployerBenefits({
        variables: {
          category: null,
          count: 30,
          journeyId
        }
      });
    }
  }, [getEmployerBenefits, journeyId, myExpert?.id]);

  useEffect(() => {
    if (
      journeyId &&
      !employerBenefitsQuery.loading &&
      employerBenefitsQuery.data?.employerBenefits.pageInfo.hasNextPage
    )
      employerBenefitsQuery.fetchMore({
        variables: {
          category: null,
          journeyId
        }
      });
  }, [employerBenefitsQuery, journeyId]);

  useEffect(() => {
    if (
      !initialLoadComplete &&
      (!journeyId ||
        (!employerBenefitsQuery.loading &&
          !getJourneyQuery.loading &&
          employerBenefitsQuery.data &&
          !employerBenefitsQuery.data.employerBenefits.pageInfo.hasNextPage))
    ) {
      setInitialLoadComplete(true);
    }
  }, [employerBenefitsQuery, getJourneyQuery, initialLoadComplete, journeyId]);

  // after the data has loaded, run the query with an empty string to get the default options
  useEffect(() => {
    if (initialLoadComplete) {
      optionsForSearch("").then(opts => {
        if (!equal(defaultOptions, opts)) {
          setDefaultOptions(opts ?? []);
        }
        if (!equal(options, opts)) {
          setOptions(opts ?? []);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete]);

  // when the query changes, re-run the query and update the options, but don't do that for an empty
  // string, where we can just use the default from the initial load
  useEffect(() => {
    if (query.length > 0) {
      optionsForSearch(query).then(opts => {
        if (!equal(options, opts)) {
          setOptions(opts ?? []);
        }
      });
    } else {
      setOptions(defaultOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <>
      <SearchInputWrapper>
        <input
          aria-label={intl.formatMessage({ id: "dictionary.search" })}
          className={[searchStyles.input, searchStyles.white].join(" ")}
          name="search"
          onChange={e => setQueryDebounced(e.target.value ?? "")}
          // onClick={e => e.stopPropagation()}
          placeholder={intl.formatMessage({ id: "dictionary.search" })}
          type="search"
        />
      </SearchInputWrapper>
      {options.length > 0 && (
        <IonList className="ion-margin-top ion-no-padding">
          {options.map(
            optionGroup =>
              optionGroup.options.length > 0 && (
                <IonItemGroup key={optionGroup.label}>
                  <IonItemDivider>
                    <IonLabel>{optionGroup.label}</IonLabel>
                  </IonItemDivider>
                  {optionGroup.options.map(option => (
                    <IonItem
                      button
                      color="primary-50"
                      key={option.value}
                      lines="full"
                      onClick={() => handleSelect(option)}
                    >
                      <IonLabel>{option.label}</IonLabel>
                      {option.groupLabel === LinkTypes.EMPLOYER_BENEFITS && (
                        <IonBadge color="secondary" slot="end">
                          <FormattedMessage id={`pages.expert.sharableUrls.itemTitles.${option.groupLabel}`} />
                        </IonBadge>
                      )}
                    </IonItem>
                  ))}
                </IonItemGroup>
              )
          )}
        </IonList>
      )}
    </>
  );
};

export default RichTextareaInAppLinksSelect;
